import { useState } from 'react'
import { MessageCircle, Send, CheckCircle } from 'lucide-react'
import { cn } from '@/shared/lib/cn'
import { respondToUserInput } from '@/shared/lib/api'

interface AskUserPromptProps {
  conversationId: number
  toolUseId: string
  question: string
  answered: boolean
  answer?: string
  onAnswered: (answer: string) => void
}

export function AskUserPrompt({ conversationId, toolUseId, question, answered, answer, onAnswered }: AskUserPromptProps) {
  const [inputValue, setInputValue] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async () => {
    if (!inputValue.trim() || isSubmitting) return

    setIsSubmitting(true)
    setError(null)

    try {
      await respondToUserInput(conversationId, toolUseId, inputValue)
      onAnswered(inputValue)
      setInputValue('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit answer')
      setIsSubmitting(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  if (answered && answer) {
    return (
      <div className="my-1.5 rounded-lg border border-emerald-500/20 bg-emerald-500/5 overflow-hidden text-xs max-w-[85%]">
        <div className="flex items-start gap-2 px-3 py-2.5">
          <CheckCircle className="w-4 h-4 flex-shrink-0 text-emerald-400 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-white/50 mb-1">{question}</div>
            <div className="text-emerald-300/80 font-medium">You answered: "{answer}"</div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="my-1.5 rounded-lg border border-violet-500/30 bg-violet-500/5 overflow-hidden text-xs max-w-[85%]">
      <div className="flex items-start gap-2 px-3 py-2.5 border-b border-violet-500/10">
        <MessageCircle className="w-4 h-4 flex-shrink-0 text-violet-400 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-violet-300/90 font-medium mb-0.5">Claude needs your input</div>
          <div className="text-white/60">{question}</div>
        </div>
      </div>
      <div className="p-3">
        <textarea
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type your answer..."
          disabled={isSubmitting}
          className={cn(
            'w-full px-2.5 py-2 rounded-lg bg-white/5 border border-white/10',
            'text-sm text-white/90 placeholder:text-white/30',
            'focus:outline-none focus:border-violet-400/40 focus:ring-1 focus:ring-violet-400/20',
            'resize-none transition-colors',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
          rows={2}
        />
        {error && (
          <div className="mt-2 text-red-400/80 text-xs">{error}</div>
        )}
        <div className="flex items-center justify-end gap-2 mt-2">
          <div className="text-white/30 text-[10px]">Press Enter to submit</div>
          <button
            onClick={handleSubmit}
            disabled={!inputValue.trim() || isSubmitting}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md',
              'bg-violet-500 text-white text-xs font-medium',
              'hover:bg-violet-400 transition-colors',
              'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-violet-500'
            )}
          >
            <Send size={12} />
            {isSubmitting ? 'Sending...' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  )
}
